import React from 'react';
import {
    Container,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import {
    Psychology,
    Visibility,
    Favorite,
    RocketLaunch,
    VisibilityOff,
} from '@mui/icons-material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import StoreButtons from '../../components/StoreButtons';
import HeartBalloonLogo from '../../components/HeartBalloonLogo';
import { detectPlatform } from '../../utils/platformDetection';
import dailyPicksImg from '../../assets/daily-picks-screen.webp';
import profileDetailsImg from '../../assets/feature-profile-details.webp';
import preferencesImg from '../../assets/feature-preferences.webp';
import mutualMatchImg from '../../assets/feature-mutual-match.webp';
import messagingImg from '../../assets/feature-messaging.webp';
import verificationImg from '../../assets/feature-verification.webp';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';

// Screens from the App Store listing; each image carries its own headline
const SCREENS = [
    { src: dailyPicksImg, alt: 'Daily Picks: a few curated profiles each day, and you pop or keep each one' },
    { src: profileDetailsImg, alt: "Profiles show personality, interests and what they're looking for" },
    { src: preferencesImg, alt: 'See partner preferences and deal-breakers before you choose' },
    { src: mutualMatchImg, alt: "It's a match when you both choose Keep" },
    { src: messagingImg, alt: 'Chat privately once you match' },
    { src: verificationImg, alt: 'Photo verification adds a verified badge to your profile' },
];

// Optional extras. Numbers match the app's tier limits (src/config/tierLimits.ts in the app) and
// the server's AI Insights limits (web/services/FeatureAccessService.js).
const EXTRAS = [
    {
        icon: <HeartBalloonLogo size={32} color="#FFD700" />,
        title: 'Golden Balloons',
        text: "Send one to someone in your picks to match straight away and start chatting, before they've kept you. 1 a month with Golden, 3 with Platinum, or buy more.",
    },
    {
        icon: <Visibility sx={{ fontSize: 32, color: LIGHT_PURPLE }} />,
        title: 'See who kept you',
        text: 'Golden and Platinum show everyone who has kept you, along with the reason they gave.',
    },
    {
        icon: <Favorite sx={{ fontSize: 32, color: VIBRANT_RED }} />,
        title: 'More picks and rewinds',
        text: '15 picks a day with Golden and 20 with Platinum, up from 10. Golden includes 5 rewinds a day to undo a pop.',
    },
    {
        icon: <RocketLaunch sx={{ fontSize: 32, color: LIGHT_PURPLE }} />,
        title: 'Boosts',
        text: 'An hour of extra visibility so more people see your profile. 2 a month with Golden, 4 with Platinum, or buy more.',
    },
    {
        icon: <Psychology sx={{ fontSize: 32, color: LIGHT_PURPLE }} />,
        title: 'AI Insights',
        text: 'Conversation health, highlights and topic ideas for any chat. 5 a month free, 15 with Golden and 30 with Platinum.',
    },
    {
        icon: <VisibilityOff sx={{ fontSize: 32, color: VIBRANT_RED }} />,
        title: 'Platinum extras',
        text: 'Incognito mode, and setting your location by address.',
    },
];

const FeaturesScreen: React.FC = () => {
    const { platform } = detectPlatform();

    return (
        <>
            <SEOHead
                title="Dating App Features | Pop The Balloon"
                description="Explore Pop The Balloon features including curated Daily Picks, Pop or Keep matching, Golden Balloons and AI dating insights."
                canonical="https://www.poptheballoonapp.com/features"
                ogTitle="Dating App Features | Pop The Balloon"
                ogDescription="Curated Daily Picks, Pop or Keep matching, likes that come with a reason, Golden Balloons and AI Insights."
                ogUrl="https://www.poptheballoonapp.com/features"
            />

            <Box sx={{
                background: 'linear-gradient(180deg, #FCFCFF 0%, #F1E4FF 100%)',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <SiteHeader />

                {/* Hero */}
                <Container maxWidth="md" sx={{ textAlign: 'center', pt: { xs: 5, md: 8 }, pb: { xs: 5, md: 7 } }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            mb: 2,
                            fontSize: { xs: '2.25rem', md: '3.25rem' },
                            lineHeight: 1.15,
                            background: `linear-gradient(135deg, ${LIGHT_PURPLE}, ${VIBRANT_RED})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                    >
                        How Pop The Balloon works
                    </Typography>
                    <Typography sx={{ mb: 4, color: 'text.secondary', fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.6 }}>
                        A few curated picks a day. Keep the ones you like and tell them why, pop the rest,
                        and start chatting when you match.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <StoreButtons placement="page" platform={platform} />
                    </Box>
                </Container>

                {/* The app's main screens: a swipeable row on phones, a grid on wider screens */}
                <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
                    <Box sx={{
                        display: 'grid',
                        gap: { xs: 2, md: 4 },
                        gridAutoFlow: { xs: 'column', md: 'row' },
                        gridAutoColumns: { xs: '72%', sm: '40%' },
                        gridTemplateColumns: { md: 'repeat(3, 1fr)' },
                        overflowX: { xs: 'auto', md: 'visible' },
                        scrollSnapType: { xs: 'x mandatory', md: 'none' },
                        pb: { xs: 2, md: 0 },
                        mx: { xs: -2, sm: 0 },
                        px: { xs: 2, sm: 0 }
                    }}>
                        {SCREENS.map(screen => (
                            <Box
                                key={screen.alt}
                                component="img"
                                src={screen.src}
                                alt={screen.alt}
                                loading="lazy"
                                width={520}
                                height={1125}
                                sx={{
                                    width: '100%',
                                    maxWidth: { md: 320 },
                                    height: 'auto',
                                    justifySelf: 'center',
                                    borderRadius: '20px',
                                    boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                                    scrollSnapAlign: 'center'
                                }}
                            />
                        ))}
                    </Box>
                </Container>

                {/* Optional extras */}
                <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
                        <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#333', mb: 1.5 }}>
                            Go further with Golden and Platinum
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 640, mx: 'auto' }}>
                            Everything above is free. These extras are optional, with monthly or annual plans
                            and one-off packs in the app.
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {EXTRAS.map(extra => (
                            <Grid item xs={12} sm={6} md={4} key={extra.title}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
                                    border: '1px solid rgba(167, 111, 255, 0.15)'
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ mb: 1.5 }}>{extra.icon}</Box>
                                        <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.2rem', mb: 1, color: '#333' }}>
                                            {extra.title}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {extra.text}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                {/* Bottom CTA */}
                <Container maxWidth="md" sx={{ pb: { xs: 6, md: 10 } }}>
                    <Card sx={{
                        textAlign: 'center',
                        py: { xs: 5, md: 7 },
                        px: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, rgba(167, 111, 255, 0.12), rgba(255, 90, 118, 0.12))',
                        border: '1px solid rgba(167, 111, 255, 0.2)',
                        boxShadow: 'none'
                    }}>
                        <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.25rem' }, color: '#333', mb: 1.5 }}>
                            Ready to give it a go?
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '1.05rem' }}>
                            Free to download. Your first picks are waiting.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <StoreButtons placement="bottom" platform={platform} />
                        </Box>
                    </Card>
                </Container>

                <AppFooter />
            </Box>
        </>
    );
};

export default FeaturesScreen;
